import type { ClientUiPublic } from '../api/platformConfig'
import { AI_PLATFORM_DUOMI, normalizeCatalogPlatformId } from '../constants/aiPlatforms'
import { isReferenceImageUploadSupported } from './bananaModel'
import type { ComicProjectCreative } from './comicCreativeBible'
import type { ComicPanel } from './comicTypes'
import { collectPageCast } from './imagePrompt/pageCast'
import { resolvePageReferenceUrls } from './imagePrompt/pageReferencePlan'

export type PageReferenceCapCheck =
  | { ok: true }
  | { ok: false; message: string }

/** 当前漫画生图模型允许的参考图张数（cap）；不支持参考图时为 0 */
export function resolveComicModelReferenceCap(
  clientUi: ClientUiPublic,
  platformId: string,
  modelId: string,
): number {
  if (!isReferenceImageUploadSupported(platformId, modelId, clientUi.imageModels)) {
    return 0
  }
  const model = clientUi.imageModels.find((row) => {
    const rowPlatform =
      normalizeCatalogPlatformId(row.platform ?? AI_PLATFORM_DUOMI) ?? AI_PLATFORM_DUOMI
    const pid = normalizeCatalogPlatformId(platformId) ?? platformId.trim()
    return row.id === modelId.trim() && rowPlatform === pid
  })
  const cap = model?.maxReferenceImages
  if (typeof cap === 'number' && cap > 0) return cap
  return 4
}

export function getPageReferenceSlotUsage(
  creative: ComicProjectCreative,
  panels: ComicPanel[],
  cap: number,
): { used: number; cap: number; remaining: number } {
  if (cap <= 0) {
    return { used: 0, cap: 0, remaining: 0 }
  }
  const { bindings } = resolvePageReferenceUrls(creative, panels, cap)
  const used = bindings.length
  return { used, cap, remaining: Math.max(0, cap - used) }
}

function planExceedsCap(
  creative: ComicProjectCreative,
  panels: ComicPanel[],
  cap: number,
): boolean {
  const { bindings } = resolvePageReferenceUrls(creative, panels, 999)
  return bindings.length > cap
}

export function pageReferenceCapExceededMessage(cap: number): string {
  return `本页参考图已达上限（${cap} 张，含场景参考与本页出场角色的造型图）。请在管理端调整模型「参考图上限」，或去掉部分参考图。`
}

export type ComicPageReferenceCapContext = {
  creative: ComicProjectCreative
  pagePanels: ComicPanel[]
  cap: number
}

/** 生图前校验：合并后是否超过模型 cap */
export function validatePageReferencesBeforeGenerate(
  creative: ComicProjectCreative,
  panels: ComicPanel[],
  cap: number,
): PageReferenceCapCheck {
  if (cap <= 0) {
    return {
      ok: false,
      message: '当前生图模型不可用或不支持参考图，请在「生图设置」中重新选择模型。',
    }
  }
  if (!planExceedsCap(creative, panels, cap)) {
    return { ok: true }
  }
  return { ok: false, message: pageReferenceCapExceededMessage(cap) }
}

function capExceededMessage(cap: number): string {
  return pageReferenceCapExceededMessage(cap)
}

/** 分镜「本格场景参考图」变更是否允许（按整页合并后判断） */
export function canSetPanelSceneReferenceUrls(
  creative: ComicProjectCreative,
  panels: ComicPanel[],
  panelId: string,
  nextUrls: string[],
  cap: number,
): PageReferenceCapCheck {
  if (cap <= 0) {
    return { ok: false, message: '当前生图模型不支持参考图，无法添加场景参考。' }
  }
  const mutated = panels.map((p) =>
    p.id === panelId
      ? {
          ...p,
          referenceImageUrls: nextUrls.length > 0 ? nextUrls : undefined,
        }
      : p,
  )
  if (!planExceedsCap(creative, mutated, cap)) {
    return { ok: true }
  }
  return { ok: false, message: capExceededMessage(cap) }
}

/** 角色卡造型参考图变更是否允许（仅当该角色在本页出场时计入 cap） */
export function canSetCharacterReferenceUrl(
  creative: ComicProjectCreative,
  panels: ComicPanel[],
  characterId: string,
  nextUrl: string | null,
  cap: number,
): PageReferenceCapCheck {
  if (cap <= 0) {
    if (nextUrl?.trim()) {
      return { ok: false, message: '当前生图模型不支持参考图，无法添加造型参考。' }
    }
    return { ok: true }
  }

  const onPage = collectPageCast(creative, panels).some((c) => c.id === characterId)
  if (!onPage) {
    return { ok: true }
  }

  const mutatedCreative: ComicProjectCreative = {
    ...creative,
    characters: creative.characters.map((c) =>
      c.id === characterId ? { ...c, referenceImageUrl: nextUrl?.trim() || null } : c,
    ),
  }

  if (!planExceedsCap(mutatedCreative, panels, cap)) {
    return { ok: true }
  }
  return { ok: false, message: capExceededMessage(cap) }
}
